<template>
  <div class="plugin-container">

    <div class="combined-navigation">
      <!-- 标签导航区域 -->
      <div class="tab-navigation">
        <div class="tabs-container">
          <div
              v-for="(tab, index) in tabs"
              :key="index"
              :class="['tab-item', { active: activeTab === index }]"
              @click="activeTab = index"
          >
            {{ tab.name }}
            <div class="active-line" v-if="activeTab === index"></div>
          </div>
        </div>

        <!-- 切换布局图标 -->
        <div class="controls-container" v-if="!isMobile">
          <!-- 视图切换 -->
          <div class="view-switch">
            <div class="view-switch-container">
              <el-icon
                  :class="['view-icon', { 'active': !listView }]"
                  @click="listView = false"
              >
                <Grid />
              </el-icon>
              <el-icon
                  :class="['view-icon', { 'active': listView }]"
                  @click="listView = true"
              >
                <Menu />
              </el-icon>
            </div>
          </div>

          <!-- 分割线 -->
          <div class="divider" v-if="!isMobile"></div>

          <!-- 分页控件 -->
          <div class="pagination" v-if="!isMobile">
            <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
            >
              <el-icon><ArrowLeft /></el-icon>
            </button>
            <span class="page-info">{{ currentPage }}/{{ totalPages }}</span>
            <button
                class="page-btn"
                :disabled="currentPage === totalPages"
                @click="goToPage(currentPage + 1)"
            >
              <el-icon><ArrowRight /></el-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-container">
        <!-- 筛选区域控制按钮 -->
        <div class="filter-control" :class="{ 'no-border': showFilters }">
          <el-button
              link
              class="filter-toggle-btn"
              @click="showFilters = !showFilters"
          >
            <el-icon :size="16" :class="['filter-icon', { rotated: showFilters }]">
              <ArrowDown />
            </el-icon>
            <span>{{ showFilters ? '收起筛选' : '展开筛选' }}</span>
          </el-button>
        </div>

        <!-- 筛选内容区域 -->
        <div class="filter-area" :class="{ 'with-border': showFilters }" v-show="showFilters">
          <div class="filter-row">
            <div class="filter-label">插件类型:</div>
            <div class="filter-options">
          <span
              v-for="(type, index) in pluginTypes"
              :key="index"
              :class="['filter-option', { active: activeFilters.type === type }]"
              @click="toggleFilter('type', type)"
          >
            {{ type }}
          </span>
            </div>
          </div>

          <div class="filter-row">
            <div class="filter-label">适用软件:</div>
            <div class="filter-options">
          <span
              v-for="(software, index) in supportedSoftware"
              :key="index"
              :class="['filter-option', { active: activeFilters.software === software }]"
              @click="toggleFilter('software', software)"
          >
            {{ software }}
          </span>
            </div>
            <div class="filter-more">
              <span>更多</span>
              <el-icon><ArrowDown /></el-icon>
            </div>
          </div>

          <div class="filter-row">
            <div class="filter-label">价格:</div>
            <div class="filter-options">
          <span
              v-for="(price, index) in priceRanges"
              :key="index"
              :class="['filter-option', { active: activeFilters.price === price }]"
              @click="toggleFilter('price', price)"
          >
            {{ price }}
          </span>
            </div>
          </div>

          <div class="filter-row">
            <div class="filter-label">筛选条件:</div>
            <div class="filter-conditions">
              <el-button size="small" class="condition-btn">
                智能排序 <el-icon><ArrowDown /></el-icon>
              </el-button>
              <el-button size="small" class="condition-btn">
                销量 <el-icon><ArrowDown /></el-icon>
              </el-button>
              <el-button size="small" class="condition-btn">
                评分 <el-icon><ArrowDown /></el-icon>
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 插件卡片展示区域 -->
    <div class="plugin-list">
      <div class="grid-container">
        <div
            v-for="(plugin, index) in paginatedPlugins"
            :key="index"
            class="plugin-card"
            @click="goToPluginDetails(plugin.id)"
        >
          <div class="card-image">
            <img :src="plugin.image" alt="插件图片">
            <div class="image-overlay">立即使用</div>
            <div class="plugin-tag" :style="{ backgroundColor: plugin.tagColor }">
              {{ plugin.tag }}
            </div>
          </div>
          <div class="card-content">
            <h3 class="plugin-title">{{ plugin.name }}</h3>
            <div class="plugin-desc">{{ plugin.description }}</div>
            <div class="plugin-meta">
              <div class="price">¥{{ plugin.price }}</div>
              <div class="sales">销量: {{ plugin.sales }}</div>
            </div>
            <div class="plugin-info">
              <div class="rating">
                <el-rate v-model="plugin.rating" disabled show-score text-color="#ff9900" />
              </div>
            </div>
            <div class="author">
              <el-avatar :size="20" :src="plugin.authorAvatar" />
              <span>{{ plugin.author }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页控件 -->
    <div class="pagination-container" v-if="!isMobile">
      <el-pagination
          background
          layout="prev, pager, next"
          :total="filteredPlugins.length"
          :page-size="pageSize"
          :current-page="currentPage"
          @current-change="handlePageChange"
      />
    </div>

  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { ref, computed, onMounted, onBeforeUnmount, } from 'vue';
import { Search, Picture, ArrowDown, Menu, Grid, ArrowLeft, ArrowRight } from '@element-plus/icons-vue';

const router = useRouter();
const searchKeyword = ref('');
const activeTab = ref(0);
const listView = ref(false);
const showFilters = ref(false); // 控制筛选区域显示
const currentPage = ref(1);
const pageSize = ref(12); // 每页显示12个插件

const activeFilters = ref({
  type: '',
  software: '',
  price: ''
});

const goToPluginDetails = (pluginId) => {
  router.push({
    path: '/PluginDetails',
    query: { id: pluginId }  // 传递插件ID作为查询参数
  });
};

// 计算总页数
const totalPages = computed(() => {
  return Math.ceil(filteredPlugins.value.length / pageSize.value);
});

// 分页跳转方法
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const tabs = ref([
  { name: '所有插件' },
  { name: '免费插件' },
  { name: '付费插件' },
  { name: '热门插件' },
  { name: '新品上架' }
]);

const pluginTypes = ref([
  '设计工具', '印前检查', '自动化处理', '色彩管理',
  '拼版工具', '文件转换', '3D预览', 'AI智能'
]);

const supportedSoftware = ref([
  'Adobe Illustrator', 'Adobe Photoshop', 'Adobe InDesign',
  'CorelDRAW', 'QuarkXPress', 'Esko', 'CGS ORIS', '海德堡'
]);

const priceRanges = ref([
  '免费', '0-100元', '100-500元', '500-1000元', '1000元以上'
]);

// 静态插件数据
const plugins = ref([
  {
    id: 1,
    name: '企业级二维码插件工具--高效赋能商业数字化',
    description: '一键生成各类包装盒型结构，支持参数化调整',
    price: 29,
    sales: 1284,
    rating: 4.7,
    image: require('@/assets/code.gif'),
    tags: ['包装设计', 'AI智能', '盒型生成'],
    type: '设计工具',
    software: 'Adobe Illustrator',
    author: '设计大师工作室',
    authorAvatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '热门',
    tagColor: '#ff4d4f'
  },
  {
    id: 2,
    name: '智能刀模生成插件--让包装设计效率飞升',
    description: '自动检测文件中的印刷问题，提高成品质量',
    price: 49,
    sales: 896,
    rating: 4.8,
    image: require('@/assets/mo.gif'),
    tags: ['印前检查', '自动化'],
    type: '印前检查',
    software: 'Adobe InDesign',
    author: '印刷质检专家',
    authorAvatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '新品',
    tagColor: '#52c41a'
  },
  {
    id: 3,
    name: '智能能效标签管理插件--数据零差错，合规全自动',
    description: '精准的色彩匹配与转换，支持多种色彩标准',
    price: 39,
    sales: 756,
    rating: 4.9,
    image: require('@/assets/neng.gif'),
    tags: ['色彩管理', '专业工具'],
    type: '色彩管理',
    software: 'Adobe Photoshop',
    author: '色彩实验室',
    authorAvatar: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '推荐',
    tagColor: '#1890ff'
  },
  {
    id: 4,
    name: '美国版能效标签三角箭头智能定位插件',
    description: '自动化拼版，节省材料，提高生产效率',
    price: 59,
    sales: 1023,
    rating: 4.6,
    image: require('@/assets/huang.gif'),
    tags: ['拼版工具', '自动化'],
    type: '拼版工具',
    software: 'Esko',
    author: '印前自动化团队',
    authorAvatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '热销',
    tagColor: '#faad14'
  },
]);

// 过滤后的插件
const filteredPlugins = computed(() => {
  let result = [...plugins.value];

  // 应用搜索关键词过滤(多条件过滤)
  if (searchKeyword.value) {
    const keyword = searchKeyword.value.toLowerCase();
    result = result.filter(plugin =>
        plugin.name.toLowerCase().includes(keyword) ||
        plugin.description.toLowerCase().includes(keyword) ||
        plugin.tags.some(tag => tag.toLowerCase().includes(keyword)
        ));
  }

  // 应用筛选条件
  if (activeFilters.value.type) {
    result = result.filter(plugin => plugin.type === activeFilters.value.type);
  }

  if (activeFilters.value.software) {
    result = result.filter(plugin => plugin.software === activeFilters.value.software);
  }

  if (activeFilters.value.price) {
    if (activeFilters.value.price === '免费') {
      result = result.filter(plugin => plugin.price === 0);
    } else if (activeFilters.value.price === '0-100元') {
      result = result.filter(plugin => plugin.price > 0 && plugin.price <= 100);
    } else if (activeFilters.value.price === '100-500元') {
      result = result.filter(plugin => plugin.price > 100 && plugin.price <= 500);
    } else if (activeFilters.value.price === '500-1000元') {
      result = result.filter(plugin => plugin.price > 500 && plugin.price <= 1000);
    } else if (activeFilters.value.price === '1000元以上') {
      result = result.filter(plugin => plugin.price > 1000);
    }
  }

  return result;
});

const performSearch = () => {
  if (searchKeyword.value.trim()) {
    console.log('执行搜索:', searchKeyword.value);
    currentPage.value = 1;
  }
};

const toggleFilter = (filterType, value) => {
  if (activeFilters.value[filterType] === value) {
    activeFilters.value[filterType] = '';
  } else {
    activeFilters.value[filterType] = value;
  }
  currentPage.value = 1;
};

const handlePageChange = (page) => {
  currentPage.value = page;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};


// 添加移动端判断变量 0814
const isMobile = ref(false);

// 添加移动端检测函数
const checkIsMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkIsMobile();
  window.addEventListener('resize', checkIsMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkIsMobile);
});

// 修改分页计算属性
const paginatedPlugins = computed(() => {
  if (isMobile.value) {
    // 移动端展示所有插件
    return filteredPlugins.value;
  } else {
    const start = (currentPage.value - 1) * pageSize.value;
    const end = start + pageSize.value;
    return filteredPlugins.value.slice(start, end);
  }
});
</script>

<style scoped>

.plugin-container {
  max-width: 1500px;
  margin: 0 auto;
  padding: 20px;
}

.logo-text div:first-child {
  font-weight: bold;
}

:deep(.custom-search-input .el-input__wrapper) {
  border-radius: 4px;
  box-shadow: 0 0 0 1px #e4e5e6 inset;
  background: #fff;
  height: 40px;
}

:deep(.custom-search-input .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #c0c4cc inset;
}

:deep(.custom-search-input .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #f54012 inset;
}

.search-tips a {
  color: #666;
  margin: 0 8px;
  text-decoration: none;
}

.search-tips a:hover {
  color: #f54012;
}

.combined-navigation {
  background: #fff;
  border-radius: 8px;
}

/* 标签导航区域样式 */
.tab-navigation {
  background: transparent;
  box-shadow: none;
  margin-bottom: 0;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tabs-container {
  display: flex;
  gap: 30px;
}

.tab-item {
  position: relative;
  cursor: pointer;
  font-size: 16px;
  color: #333;
  padding: 5px 0;
  transition: color 0.3s;
}

.tab-item:hover, .tab-item.active {
  color: #f54012;
}

.active-line {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #f54012;
  border-radius: 2px;
}

.controls-container {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* 切换视图样式 */
.view-switch-container {
  display: flex;
  gap: 8px;
  padding: 4px;
}

.view-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #606266;
  transition: all 0.3s;
}

.view-icon.active {
  color: #f54012;
}

/* 分割线样式 */
.divider {
  width: 1px;
  height: 15px;
  background: #dcdfe6;
}

/* 分页控件样式 */
.pagination {
  display: flex;
  align-items: center;
  gap: 5px;
}

.page-btn {
  background: transparent !important; /* 去除背景色 */
  border: none !important;
  width: 24px;
  height: 24px;
  font-size: 16px;
  font-weight: bold;
  color: #01030b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s;
}

.page-btn:hover:not(:disabled) {
  color: #f54012; /* 悬停时变为主题色 */
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-btn .el-icon {
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-info {
  font-size: 14px;
  color: #666;
}

/* 筛选区域容器 */
.filter-container {
  background: transparent;
  box-shadow: none;
  padding: 8px 0;
  margin-bottom: 20px;
}

/* 筛选区域控制按钮 */
.filter-control {
  padding: 4px 20px;
  display: flex;
  justify-content: flex-end;
  border-bottom: 1px solid #f0f0f0;
}

.filter-control.no-border {
  border-bottom: none !important;
}

.filter-toggle-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #666;
  font-size: 14px;
  transition: all 0.3s;
}

.filter-toggle-btn:hover {
  color: #f54012;
}

.filter-icon {
  transition: transform 0.3s;
}

.filter-icon.rotated {
  transform: rotate(180deg);
}

/* 筛选内容区域样式 */
.filter-area {
  background: #fff;
  padding: 4px 20px;
  margin-bottom: 20px;
  border-bottom: none;
}

.filter-area.with-border {
  border-bottom: 1px solid #f0f0f0;
}

.filter-row {
  display: flex;
  padding: 8px 0;
}

.filter-row:last-child {
  border-bottom: none;
}

.filter-label {
  width: 80px;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: bold;
  color: #01030b;
  padding-top: 5px;
}

.filter-options {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-option {
  font-size: 14px;
  color: #333;
  cursor: pointer;
  padding: 5px 12px;
  transition: all 0.2s;
}

.filter-option:hover {
  color: #f54012;
  border-color: #f54012;
}

.filter-option.active {
  background: #fff5f2;
  color: #f54012;
  border-color: #f54012;
  font-weight: 500;
}

.filter-more {
  display: flex;
  align-items: center;
  gap: 3px;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  padding: 5px 12px;
  transition: all 0.2s;
}

.filter-more:hover {
  color: #f54012;
  border-color: #f54012;
}

.filter-conditions {
  flex: 1;
  display: flex;
  gap: 10px;
}

.condition-btn {
  display: flex;
  align-items: center;
  gap: 3px;
  color: #666;
  border: 1px solid #dcdfe6;
}

.condition-btn:hover {
  color: #f54012;
  border-color: #f54012;
}

/* 插件卡片区域样式 */
.plugin-list {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 20px;
}

.plugin-card {
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
}

.plugin-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.card-image {
  position: relative;
  height: 150px;
  overflow: hidden;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.plugin-card:hover .card-image img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(245, 64, 18, 0.85);
  color: white;
  text-align: center;
  padding: 8px 0;
  font-size: 14px;
  transform: translateY(100%);
  transition: transform 0.3s;
}

.plugin-card:hover .image-overlay {
  transform: translateY(0);
}

.plugin-tag {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 8px;
  border-radius: 3px;
  color: white;
  font-size: 12px;
  font-weight: bold;
  z-index: 2;
}

.card-content {
  padding: 15px;
  display: flex; /* 添加flex布局 */
  flex-direction: column; /* 垂直排列 */
  flex-grow: 1; /* 允许内容区域扩展 */
}
.plugin-title {
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 8px 0;
  color: #333;
  /* 移除固定高度和行数限制 */
  min-height: 20px; /* 设置最小高度 */
  overflow: visible; /* 允许内容溢出 */
  display: block; /* 恢复默认显示方式 */
  -webkit-line-clamp: unset; /* 移除行数限制 */
}

.plugin-desc {
  font-size: 12px;
  color: #666;
  margin-bottom: 10px;
  /* 移除固定高度和行数限制 */
  overflow: visible; /* 允许内容溢出 */
  display: block; /* 恢复默认显示方式 */
  -webkit-line-clamp: unset; /* 移除行数限制 */
  flex-grow: 1; /* 允许描述区域扩展 */
}

.plugin-card {
  display: flex; /* 启用flex布局 */
  flex-direction: column; /* 垂直排列 */
  height: auto; /* 高度自适应 */
  min-height: 350px; /* 设置最小高度保证一致性 */
}

.plugin-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.price {
  font-size: 16px;
  font-weight: bold;
  color: #f54012;
}

.sales {
  font-size: 12px;
  color: #999;
}

.plugin-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rating {
  display: flex;
  align-items: center;
}

.rating-text {
  font-size: 12px;
  color: #ff9900;
  margin-left: 5px;
  font-weight: bold;
}

.author {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #666;
}

:deep(.el-rate) {
  --el-rate-icon-size: 14px;
}

/* 分页控件 */
.pagination-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .grid-container {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 992px) {
  .grid-container {
    grid-template-columns: repeat(3, 1fr);
  }

  .header-content {
    flex-direction: column;
    gap: 15px;
  }

  .tabs-container {
    gap: 15px;
  }

  .controls-container {
    gap: 10px;
  }
}

@media (max-width: 768px) {
  .grid-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .pagination-container {
    display: none;
  }

  .controls-container .pagination {
    display: none;
  }

  .filter-container {
    padding: 8px 0;
  }

  .tab-navigation {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .filter-row {
    flex-direction: column;
    gap: 10px;
  }

  .filter-label {
    width: 100%;
  }

  .filter-options {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .grid-container {
    grid-template-columns: 1fr;
  }

  .tabs-container {
    flex-wrap: wrap;
    gap: 10px;
  }

  .tab-item {
    font-size: 14px;
  }
}
</style>